import React, { useEffect, useState, useMemo } from "react";
import axios from "../context/axiosConfig";
import requireAuth from "../context/requireAuth";
import MaterialReactTable from 'material-react-table';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ExportToCsv } from 'export-to-csv';

function PlantMovementLogs() {
  const org_id = localStorage.getItem("organization");
  
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showDestroyedOnly, setShowDestroyedOnly] = useState(false);
  const [movementLogs, setMovementLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPlotNo, setSelectedPlotNo] = useState('');
  const [plants, setPlants] = useState({});
  const [plots, setPlots] = useState([]);

  // Fetch plants data to map plant_id to plant details
  useEffect(() => {
    axios.get(`orgs/${org_id}/plants/`, { withCredentials: true })
      .then(res => {
        const plantsMap = res.data.reduce((acc, plant) => {
          acc[plant.plant_id] = plant;
          return acc;
        }, {});
        setPlants(plantsMap);
      })
      .catch(err => console.error('Error fetching plants:', err));
  }, [org_id]);

  // Fetch plots data for the dropdown
  useEffect(() => {
    axios.get(`orgs/${org_id}/plots/`, { withCredentials: true })
      .then(res => {
        setPlots(res.data);
      })
      .catch(err => console.error('Error fetching plots:', err));
  }, [org_id]);

  // Fetch movement logs
  useEffect(() => {
    setLoading(true);
    let url = `orgs/${org_id}/plants/movement-logs`;
    const params = new URLSearchParams();
    
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (selectedPlotNo) params.append('plot_no', selectedPlotNo);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    axios.get(url, { withCredentials: true })
      .then(res => {
        console.log('Movement logs response:', res.data);
        // Enrich logs with plant data
        const enrichedLogs = res.data.map(log => ({
          ...log,
          plant_data: plants[log.plant_id] || {},
        }));
        setMovementLogs(enrichedLogs);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching movement logs:', err);
        setMovementLogs([]);
        setLoading(false);
      });
  }, [org_id, fromDate, toDate, selectedPlotNo, plants]);

  // Filtering logic for active/destroyed plants
  const filteredMovementLogs = useMemo(() => {
    if (showActiveOnly) return movementLogs.filter(log => !log.plant_data.datedestroyed);
    if (showDestroyedOnly) return movementLogs.filter(log => log.plant_data.datedestroyed);
    return movementLogs;
  }, [movementLogs, showActiveOnly, showDestroyedOnly]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'org_plant_no',
        header: 'Plant Number',
        Cell: ({ row }) => {
          const value = row.original.org_plant_no;
          return value !== null && value !== undefined ? String(value) : "N/A";
        },
        filterFn: 'equals'
      },
      {
        accessorFn: (row) => row.plant_data?.variety || 'N/A',
        id: 'variety',
        header: 'Variety',
        Cell: ({ row }) => row.original.plant_data?.variety || 'N/A',
        filterFn: 'includesString'
      },
      {
        accessorKey: 'date_moved',
        header: 'Date Moved',
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }) : 'N/A';
        }
      },
      {
        accessorFn: (row) => {
          const value = row.from_plot;
          return value !== null && value !== undefined ? String(value) : 'N/A';
        },
        id: 'from_plot',
        header: 'From Plot',
        Cell: ({ row }) => {
          const value = row.original.from_plot;
          return value !== null && value !== undefined ? String(value) : 'N/A';
        },
        filterFn: 'includesString'
      },
      {
        accessorKey: 'from_location',
        header: 'From Location',
        Cell: ({ cell }) => cell.getValue() || 'N/A',
        filterFn: 'includesString'
      },
      {
        accessorFn: (row) => {
          const value = row.to_plot;
          return value !== null && value !== undefined ? String(value) : 'N/A';
        },
        id: 'to_plot',
        header: 'To Plot',
        Cell: ({ row }) => {
          const value = row.original.to_plot;
          return value !== null && value !== undefined ? String(value) : 'N/A';
        },
        filterFn: 'includesString'
      },
      {
        accessorKey: 'to_location',
        header: 'To Location',
        Cell: ({ cell }) => cell.getValue() || 'N/A',
        filterFn: 'includesString'
      },
      {
        accessorKey: 'org_plot_no',
        header: 'Plot Number',
        Cell: ({ cell }) => cell.getValue() || 'N/A',
        filterFn: 'equals'
      },
    ],
    [plants],
  );

  const handleExportData = () => {
    const csvOptions = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: true,
      filename: `plant-movement-logs-${new Date().toISOString().split('T')[0]}`,
    };

    const exportData = filteredMovementLogs.map(log => ({
      plant_number: log.org_plant_no || 'N/A',
      variety: log.plant_data?.variety || 'N/A',
      date_moved: log.date_moved ? new Date(log.date_moved).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }) : 'N/A',
      from_plot: log.from_plot || 'N/A',
      from_location: log.from_location || 'N/A',
      to_plot: log.to_plot || 'N/A',
      to_location: log.to_location || 'N/A',
      plot_number: log.org_plot_no || 'N/A',
    }));

    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(exportData);
  };

  const handleExportSelected = (rows) => {
    const csvOptions = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: true,
      filename: `selected-movement-logs-${new Date().toISOString().split('T')[0]}`,
    };

    const exportData = rows.map(row => ({
      plant_number: row.original.org_plant_no || 'N/A',
      variety: row.original.plant_data?.variety || 'N/A',
      date_moved: row.original.date_moved ? new Date(row.original.date_moved).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }) : 'N/A',
      from_plot: row.original.from_plot || 'N/A',
      from_location: row.original.from_location || 'N/A',
      to_plot: row.original.to_plot || 'N/A',
      to_location: row.original.to_location || 'N/A',
      plot_number: row.original.org_plot_no || 'N/A',
    }));

    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(exportData);
  };

  return (
    <div className="bodyContent">
      <label className="subheader">Plant Movement Logs</label>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filter Movement Logs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="plot-select-label">Plot</InputLabel>
            <Select
              labelId="plot-select-label"
              value={selectedPlotNo}
              onChange={(e) => setSelectedPlotNo(e.target.value)}
              label="Plot"
            >
              <MenuItem value="">
                <em>All Plots</em>
              </MenuItem>
              {plots.filter(plot => plot.plotno !== "1").map((plot) => (
                <MenuItem key={plot.plot_id} value={plot.org_plot_no}>
                  {plot.plotno || `Plot ${plot.org_plot_no}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={() => {
              setFromDate('');
              setToDate('');
              setSelectedPlotNo('');
            }}
            variant="outlined"
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
          <FormControl component="fieldset">
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <Checkbox
                  size="small"
                  type="checkbox"
                  className="maintenanceCheckbox maintenanceTableCheckbox"
                  checked={showActiveOnly}
                  onChange={() => {
                    setShowActiveOnly(prev => !prev);
                    if (!showActiveOnly) setShowDestroyedOnly(false);
                  }}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                />
                Show only active plants
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <Checkbox
                  size="small"
                  type="checkbox"
                  className="maintenanceCheckbox maintenanceTableCheckbox"
                  checked={showDestroyedOnly}
                  onChange={() => {
                    setShowDestroyedOnly(prev => !prev);
                    if (!showDestroyedOnly) setShowActiveOnly(false);
                  }}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                />
                Show only destroyed plants
              </label>
            </Box>
          </FormControl>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {loading 
            ? 'Loading movement logs...' 
            : `Showing ${filteredMovementLogs.length} movement log${filteredMovementLogs.length !== 1 ? 's' : ''}`
          }
        </Typography>
      </Paper>

      <div className="plantTableDiv">
        <MaterialReactTable
          columns={columns}
          data={filteredMovementLogs}
          enableRowSelection
          enableColumnOrdering
          enableSorting
          enableColumnFilters
          state={{
            isLoading: loading,
          }}
          initialState={{
            showColumnFilters: true,
            pagination: { pageIndex: 0, pageSize: 50 },
            sorting: [{ id: 'date_moved', desc: true }]
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
            >
              <Button
                color="primary"
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                variant="contained"
                disabled={movementLogs.length === 0}
              >
                Export All Logs
              </Button>
              <Button
                disabled={
                  !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                }
                onClick={() => handleExportSelected(table.getSelectedRowModel().rows)}
                startIcon={<FileDownloadIcon />}
                variant="contained"
                color="secondary"
              >
                Export Selected Logs
              </Button>
            </Box>
          )}
        />
      </div>
    </div>
  );
}

export default requireAuth(PlantMovementLogs);